import type { PreScreeningData } from '@/context/pre-screening-context';
import { usePrescreeningData } from '@/context/pre-screening-context';
import { CtaButton, CtaButtonText, CtaSection } from '@/pages/components/styled/Cta';
import { StyledDialog } from '@/pages/components/styled/Dialog';
import { formatPhoneNumberToInternational } from '@/utils/phone-number';
import { Typography } from '@montugroup/design-system';
import CloseIcon from '@mui/icons-material/Close';
import { Box, DialogContent, DialogTitle, IconButton, Slide, styled } from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import React from 'react';

interface ConfirmSmsNumberDialogProps {
  open: boolean;
  onClose: (trackEvent: boolean) => void;
  onSubmit: (data: PreScreeningData['patientDetails']) => void;
}

const DialogSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  marginTop: theme.spacing(8),
}));

const StyledHeading = styled(Typography)(({ theme }) => ({
  fontSize: theme.spacing(4.5),
  fontWeight: 'bold',
  color: theme.palette.primary.main,
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  lineHeight: 1.5,
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ConfirmSmsNumberDialog = ({ open, onClose, onSubmit }: ConfirmSmsNumberDialogProps) => {
  const { preScreeningData } = usePrescreeningData();

  return (
    <StyledDialog
      open={open}
      onClose={() => onClose(false)}
      TransitionComponent={Transition}
      data-testid="confirm-sms-number-dialog"
    >
      <DialogTitle sx={{ position: 'relative', pr: 5 }}>
        <IconButton
          aria-label="close"
          onClick={() => onClose(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
          data-testid="close-confirm-sms-number-dialog-button"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box flex={1}>
          <DialogSection>
            <StyledHeading>The phone number you entered is:</StyledHeading>
            <StyledHeading>
              {formatPhoneNumberToInternational(preScreeningData?.patientDetails.phone ?? '')}
            </StyledHeading>
            <StyledTypography>If this is correct, proceed to send SMS verification.</StyledTypography>
          </DialogSection>
        </Box>
        <CtaSection breakpoint="sm">
          <CtaButton
            type="submit"
            variant="contained"
            onClick={() => {
              onSubmit(preScreeningData?.patientDetails);
            }}
            breakpoint="sm"
            data-testid="confirm-sms-number-button"
          >
            <CtaButtonText breakpoint="sm" fontWeight="bold">
              Send SMS verification
            </CtaButtonText>
          </CtaButton>
          <CtaButton
            type="submit"
            variant="outlined"
            onClick={() => onClose(true)}
            breakpoint="sm"
            data-testid="sms-back-button"
          >
            <CtaButtonText breakpoint="sm">Back</CtaButtonText>
          </CtaButton>
        </CtaSection>
      </DialogContent>
    </StyledDialog>
  );
};

export default ConfirmSmsNumberDialog;
